import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Store, CheckCircle } from "lucide-react";
import type { AppMode } from "@/types/convex";

interface DeviceAppsSettingsContentProps {
  deviceId?: string;
  appMode: AppMode;
  onAppModeChange: (mode: AppMode) => Promise<void>;
}

export const DeviceAppsSettingsContent = ({
  appMode,
  onAppModeChange
}: DeviceAppsSettingsContentProps) => {
  const handleModeChange = (value: string) => {
    onAppModeChange(value as AppMode);
  };

  const getModeDescription = (mode: AppMode): string => {
    switch (mode) {
      case "categories":
        return "Kosher App Store enforces app filtering based on categories, whitelists, and blacklists configured for this device's level.";
      case "whitelist":
        return "Only specifically approved apps can be installed. All other apps are blocked regardless of category.";
      case "blacklist":
        return "Apple App Store allows unrestricted app downloads. Only blacklisted apps are blocked.";
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">App Settings</h2>
          <p className="text-muted-foreground mt-1">Configure app store and installation preferences</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              App Mode Selection
            </CardTitle>
            <CardDescription>
              Choose how apps are filtered and managed for this device
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <RadioGroup
                value={appMode}
                onValueChange={handleModeChange}
                className="space-y-3"
              >
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="categories" id="app-categories" />
                  <div className="flex-1">
                    <Label htmlFor="app-categories" className="flex items-center gap-2 font-medium cursor-pointer">
                      <Store className="w-4 h-4" />
                      Categories (Kosher App Store)
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Filter apps based on category rules. Access to whitelist, blacklist, and category settings.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="whitelist" id="app-whitelist" />
                  <div className="flex-1">
                    <Label htmlFor="app-whitelist" className="flex items-center gap-2 font-medium cursor-pointer">
                      <CheckCircle className="w-4 h-4" />
                      Whitelist Only
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Only allow specifically approved apps. All other apps are blocked.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="blacklist" id="app-blacklist" />
                  <div className="flex-1">
                    <Label htmlFor="app-blacklist" className="flex items-center gap-2 font-medium cursor-pointer">
                      <Store className="w-4 h-4" />
                      Apple App Store
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Unrestricted app downloads from the Apple App Store. Only blacklisted apps are blocked.
                    </p>
                  </div>
                </div>
              </RadioGroup>

              <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-sm text-primary">
                  <strong>Current Mode:</strong> {getModeDescription(appMode)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

    </>
  );
};
